// # Note Media Component - Handles media display and uploads
import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { storage, db } from '../../auth/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { FaImage, FaYoutube, FaTimesCircle, FaTrash, FaSearch, FaSearchMinus, FaSearchPlus, FaTimes, FaUndo } from 'react-icons/fa';
import './notemedia.css';
import DashboardHeader from '../../components/DashboardHeader';
import cacheManager from '../../auth/cache_manager';

// # Use forwardRef to expose functions to parent component
const NoteMedia = forwardRef(({ onMediaAdd, noteMedia, onMediaRemove, noteId }, ref) => {
  const { stepId, chapterId } = useParams();
  const { user } = useAuthStore();
  const fileInputRef = useRef(null);
  const imageModalRef = useRef(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [showYoutubeInput, setShowYoutubeInput] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # Check if an image already exists in the media array
  const hasImage = noteMedia.some(media => media.type === 'image');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Cleanup temporary object URLs when component unmounts
  useEffect(() => {
    // # Return cleanup function
    return () => {
      // # Revoke any temporary object URLs to prevent memory leaks
      noteMedia.forEach(media => {
        if (media.type === 'image' && media.isTemp && media.url) {
          try {
            URL.revokeObjectURL(media.url);
            console.log(`Cleanup: Revoked temporary URL for image: ${media.id}`);
          } catch (error) {
            console.error(`Cleanup: Error revoking object URL: ${error}`);
          }
        }
      });
    };
  }, [noteMedia]);

  // # Expose functions to parent component through the ref
  useImperativeHandle(ref, () => ({
    // # Export the uploadTempImages function
    uploadTempImages: async () => {
      return await uploadTempImages();
    }
  }));

  // # Process an image file locally without uploading to storage yet
  const handleImageSelect = useCallback(async (file) => {
    if (!file) return;
    
    // # Check if there's already an image - return if true
    if (hasImage) {
      alert('Only one image per note is allowed. Please remove the existing image first.');
      return;
    }

    // # Check file size before attempting compression - reject files over 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('File is too large. Please select an image smaller than 10MB.');
      return;
    }
    
    try {
      setIsUploading(true);
      
      // # Compress the image (client-side compression)
      const compressedFile = await compressImage(file, 380 * 1024); // # Compress to target 380KB
      
      // # Generate unique ID for the image
      const timestamp = new Date().getTime();
      const uniqueChars = Math.random().toString(36).substring(2, 7);
      const imageId = `img_${timestamp}_${uniqueChars}`;
      
      // # Get file extension from the file or use default
      const fileExtension = compressedFile.type.split('/')[1] || 'jpeg';
      
      // # Generate a noteId if it doesn't exist (using proper noteId from props or generate a temporary one)
      const noteIdToUse = noteId || `temp_${Date.now()}`;
      
      // # Create storage reference with correct path: notes/${uid}/noteid/file.format
      const fileName = `image.${fileExtension}`;
      const storagePath = `notes/${user.uid}/${noteIdToUse}/${fileName}`;
      const storageRef = ref(storage, storagePath);
      
      // # Create a local URL for preview while uploading
      const localUrl = URL.createObjectURL(compressedFile);
      
      // # Add the image to the note's media array with temporary URL first
      onMediaAdd({
        type: 'image',
        url: localUrl,
        id: imageId,
        tempUpload: true // # Flag to indicate this image is being uploaded
      });
      
      // # Upload the file
      const uploadTask = uploadBytesResumable(storageRef, compressedFile);
      
      // # Monitor upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Error uploading image:', error);
          // # If upload fails, remove the media
          onMediaRemove(imageId);
          setIsUploading(false);
          alert('Error uploading image. Please try again.');
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.ref);
            
            // # Update the media with the permanent URL and path
            onMediaRemove(imageId); // Remove the temporary entry
            onMediaAdd({
              type: 'image',
              url: downloadURL,
              id: imageId,
              path: storagePath,
              format: fileExtension
            });
            
            // # Revoke the temporary object URL
            URL.revokeObjectURL(localUrl);
            
            setIsUploading(false);
            setUploadProgress(0);
            console.log(`Uploaded image: ${storagePath}`);
          } catch (error) {
            console.error('Error getting download URL:', error);
            setIsUploading(false);
            alert('Error processing uploaded image. Please try again.');
          }
        }
      );
    } catch (error) {
      console.error('Error handling image selection:', error);
      alert('Error processing image. Please try again with a smaller image.');
      setIsUploading(false);
    }
  }, [hasImage, onMediaAdd, onMediaRemove, noteId, user]);

  // # Updated function to handle any remaining temporary images
  // # This is kept for backward compatibility but should no longer be needed for normal operation
  const uploadTempImages = async () => {
    if (!user) return [...noteMedia];
    
    const updatedMedia = [...noteMedia];
    setUploadProgress(0);
    
    // # Check if there are any temporary images that need uploading
    const hasTempImages = updatedMedia.some(media => 
      media.type === 'image' && media.isTemp && media.file
    );
    
    if (!hasTempImages) {
      // # No temporary images to upload, return cleaned media array
      return updatedMedia.map(media => {
        const cleanedMedia = {
          type: media.type || 'unknown',
          url: media.url || '',
          id: media.id || `media_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
        };
        
        if (media.path) cleanedMedia.path = media.path;
        if (media.format) cleanedMedia.format = media.format;
        
        return cleanedMedia;
      });
    }
    
    // # Process each media item and upload any temporary images
    for (let i = 0; i < updatedMedia.length; i++) {
      const media = updatedMedia[i];
      
      // # Only process temporary images
      if (media.type === 'image' && media.isTemp && media.file) {
        try {
          setIsUploading(true);
          
          // # Get file extension from the file or use default
          const fileExtension = media.file.type.split('/')[1] || 'jpeg';
          
          // # Generate a noteId if it doesn't exist (using proper noteId from props or generate a temporary one)
          const noteIdToUse = noteId || `temp_${Date.now()}`;
          
          // # Create storage reference with correct path: notes/${uid}/noteid/file.format
          const fileName = `image.${fileExtension}`;
          const storagePath = `notes/${user.uid}/${noteIdToUse}/${fileName}`;
          const storageRef = ref(storage, storagePath);
          
          // # Upload the file
          const uploadTask = uploadBytesResumable(storageRef, media.file);
          
          // # Monitor upload progress
          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                console.error('Error uploading image:', error);
                reject(error);
              },
              async () => {
                try {
                  const downloadURL = await getDownloadURL(uploadTask.ref);
                  
                  // # Update the media item without the file property
                  updatedMedia[i] = {
                    type: 'image',
                    url: downloadURL,
                    id: media.id,
                    path: storagePath,
                    format: fileExtension // # Store the format for future reference
                  };
                  
                  // # Revoke the temporary object URL
                  if (media.url) {
                    URL.revokeObjectURL(media.url);
                  }
                  
                  resolve();
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
          
          console.log(`Uploaded image: ${storagePath}`);
        } catch (error) {
          console.error('Error uploading temporary image:', error);
          // # Continue with other uploads even if one fails
        }
      }
    }
    
    setIsUploading(false);
    setUploadProgress(0);
    
    // # Return cleaned media array without any temporary files or Blob objects
    return updatedMedia.map(media => {
      // # Create a cleaned media object with only the necessary properties
      const cleanedMedia = {
        type: media.type || 'unknown',
        url: media.url || '',
        id: media.id || `media_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
      };
      
      // # Only include path if it exists and is not undefined
      if (media.path) {
        cleanedMedia.path = media.path;
      }
      
      // # Include format if available
      if (media.format) {
        cleanedMedia.format = media.format;
      }
      
      return cleanedMedia;
    });
  };

  // # Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  // # Image compression function
  const compressImage = (file, maxSizeBytes) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // # Calculate scaling factor to maintain aspect ratio
          const aspectRatio = width / height;
          
          // # Start with the original dimensions
          let newWidth = width;
          let newHeight = height;
          
          // # If the image is larger than 1200px in either dimension, scale it down
          const maxDimension = 1200;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              newWidth = maxDimension;
              newHeight = newWidth / aspectRatio;
            } else {
              newHeight = maxDimension;
              newWidth = newHeight * aspectRatio;
            }
          }
          
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          
          // # Start with high quality
          let quality = 0.9;
          let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          let attempts = 0;
          
          // # Iteratively reduce quality until file size is under maxSizeBytes
          const reduceQuality = () => {
            const binary = atob(compressedDataUrl.split(',')[1]);
            const binarySize = binary.length;
            
            if (binarySize <= maxSizeBytes || quality <= 0.1 || attempts >= 10) {
              // # Convert data URL to Blob
              const array = [];
              for (let i = 0; i < binary.length; i++) {
                array.push(binary.charCodeAt(i));
              }
              
              resolve(new Blob([new Uint8Array(array)], { type: 'image/jpeg' }));
            } else {
              // # Reduce quality more aggressively for larger files
              const overageRatio = binarySize / maxSizeBytes;
              if (overageRatio > 5) {
                quality -= 0.2;
              } else if (overageRatio > 2) {
                quality -= 0.1;
              } else {
                quality -= 0.05;
              }
              
              // # Ensure quality doesn't go below 0.1
              quality = Math.max(0.1, quality);
              
              compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
              attempts++;
              
              // # Continue reduction process
              reduceQuality();
            }
          };
          
          reduceQuality();
        };
      };
    });
  };

  // # Handle media remove with storage cleanup
  const handleMediaRemove = async (mediaId) => {
    // # Find the media item to remove
    const mediaToRemove = noteMedia.find(media => media.id === mediaId);
    
    if (mediaToRemove) {
      try {
        // # If it's a permanent image (already in storage), delete from storage
        if (mediaToRemove.type === 'image' && mediaToRemove.path) {
          try {
            const fileRef = ref(storage, mediaToRemove.path);
            await deleteObject(fileRef);
            console.log(`Deleted media file: ${mediaToRemove.path}`);
          } catch (error) {
            console.error(`Error deleting media file: ${mediaToRemove.path}`, error);
            // # Continue with removal even if deletion fails
          }
        }
        
        // # If it's a temporary URL (from URL.createObjectURL), revoke it
        if (mediaToRemove.type === 'image' && 
            (mediaToRemove.isTemp || mediaToRemove.tempUpload) && 
            mediaToRemove.url && 
            mediaToRemove.url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(mediaToRemove.url);
            console.log(`Revoked temporary URL for image: ${mediaId}`);
          } catch (error) {
            console.error(`Error revoking object URL: ${error}`);
          }
        }
        
        // # Call the parent's remove function to update state
        onMediaRemove(mediaId);
      } catch (error) {
        console.error('Error removing media:', error);
        alert('Error removing media. Please try again.');
      }
    } else {
      // # Just call the remove function if media item not found
      onMediaRemove(mediaId);
    }
  };

  // # Handle YouTube URL add
  const addYoutubeVideo = () => {
    if (!youtubeUrl) return;
    
    // # Extract Video ID from various YouTube URL formats
    let videoId = '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = youtubeUrl.match(regExp);
    
    if (match && match[2].length === 11) {
      videoId = match[2];
      
      // # Generate timestamp for unique ID
      const timestamp = Date.now();
      const uniqueId = `yt_${videoId}_${timestamp}`;
      
      // # Call the onMediaAdd callback with the YouTube ID
      onMediaAdd({
        type: 'youtube',
        url: `https://www.youtube.com/embed/${videoId}`,
        id: uniqueId,
        // # Add empty path property to maintain consistency with image objects
        // # but avoid sending undefined values to Firestore
        path: null
      });
      
      // # Reset and hide input
      setYoutubeUrl('');
      setShowYoutubeInput(false);
    } else {
      alert('Please enter a valid YouTube URL');
    }
  };
  
  // # Image modal functions
  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setZoomLevel(1);
    setImagePos({ x: 0, y: 0 });
  };
  
  const closeImageModal = () => {
    setModalImage(null);
    setZoomLevel(1);
    setImagePos({ x: 0, y: 0 });
  };
  
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePos({ x: 0, y: 0 });
  };
  
  // # Drag functionality
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStartPos({
        x: e.clientX - imagePos.x,
        y: e.clientY - imagePos.y
      });
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStartPos.x;
      const newY = e.clientY - dragStartPos.y;
      setImagePos({ x: newX, y: newY });
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // # Stop click events from bubbling when dragging
  const handleImageClick = (e) => {
    if (isDragging) {
      e.stopPropagation();
    }
  };
  
  // # Add event listeners for mouse up even outside the component
  useEffect(() => {
    if (modalImage) {
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };
      
      window.addEventListener('mouseup', handleGlobalMouseUp);
      
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [modalImage]);

  // # Render media items
  const renderMediaItems = () => {
    if (!noteMedia || noteMedia.length === 0) {
      return (
        <div className="nm_no_media">
          <p>No media added yet</p>
        </div>
      );
    }

    return (
      <div className="nm_media_grid">
        {noteMedia.map((media) => (
          <div key={media.id} className="nm_media_item">
            {media.type === 'image' ? (
              <div className="nm_image_container">
                <img 
                  src={media.url} 
                  alt="Note media" 
                  className="nm_image" 
                  onClick={() => openImageModal(media.url)}
                />
                <button 
                  className="nm_remove_button" 
                  onClick={() => handleMediaRemove(media.id)}
                  title="Remove image"
                >
                  <FaTrash />
                </button>
                <button
                  className="nm_expand_button"
                  onClick={() => openImageModal(media.url)}
                  title="Expand image"
                >
                  <FaSearch />
                </button>
                {media.isTemp && (
                  <div className="nm_temp_indicator">Pending Upload</div>
                )}
              </div>
            ) : (
              <div className="nm_youtube_container">
                <iframe
                  src={media.url}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                  className="nm_youtube_iframe"
                ></iframe>
                <button 
                  className="nm_remove_button" 
                  onClick={() => handleMediaRemove(media.id)}
                  title="Remove video"
                >
                  <FaTrash />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  // # Image modal component
  const ImageModal = () => {
    if (!modalImage) return null;
    
    return (
      <div 
        className="npg_image_modal_overlay" 
        onClick={closeImageModal}
      >
        <div 
          className="npg_image_modal_container" 
          onClick={(e) => e.stopPropagation()}
          ref={imageModalRef}
        >
          <div 
            className="npg_image_modal_content"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <img 
              src={modalImage} 
              alt="Expanded view" 
              className="npg_image_modal_img" 
              style={{ 
                transform: `scale(${zoomLevel}) translate(${imagePos.x}px, ${imagePos.y}px)`,
                cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default')
              }}
              onClick={handleImageClick}
            />
          </div>
          <button 
            className="npg_image_modal_close" 
            onClick={closeImageModal}
            title="Close"
          >
            <FaTimes />
          </button>
          <div className="npg_image_modal_controls">
            <button 
              className="npg_image_modal_zoom_out" 
              onClick={handleZoomOut}
              title="Zoom out"
              disabled={zoomLevel <= 0.5}
            >
              <FaSearchMinus />
            </button>
            <div className="npg_image_modal_zoom_level">
              {Math.round(zoomLevel * 100)}%
            </div>
            <button 
              className="npg_image_modal_zoom_in" 
              onClick={handleZoomIn}
              title="Zoom in"
              disabled={zoomLevel >= 3}
            >
              <FaSearchPlus />
            </button>
            <button 
              className="npg_image_modal_reset" 
              onClick={handleResetZoom}
              title="Reset view"
            >
              <FaUndo />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`nm_container ${isDarkMode ? 'nm_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="nm_media_display">
        <h3 className="nm_section_title">Note Media</h3>
        {renderMediaItems()}
      </div>
      
      <div className="nm_media_controls">
        {/* Image upload box */}
        <div className="nm_media_box nm_image_box">
          <h3>Add Image</h3>
          <div className="nm_media_content">
            {isUploading ? (
              <div className="nm_upload_progress">
                <div className="nm_upload_progress_inner">
                  <div 
                    className="nm_upload_progress_bar" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="nm_upload_progress_text">
                  {uploadProgress > 0 ? `Uploading image: ${Math.round(uploadProgress)}%` : 'Processing image...'}
                </div>
              </div>
            ) : (
              <>
                <button 
                  className={`nm_media_button ${hasImage ? 'nm_button_disabled' : ''}`}
                  onClick={() => !hasImage && fileInputRef.current.click()}
                  disabled={hasImage}
                >
                  <FaImage /> {hasImage ? 'One Image Limit Reached' : 'Choose Image'}
                </button>
                <p className="nm_media_description">
                  {hasImage 
                    ? 'Only one image per note is allowed. Remove existing image to add a new one.' 
                    : 'Click to select and upload an image to your note'}
                </p>
              </>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleFileInputChange}
              disabled={hasImage}
            />
          </div>
        </div>
        
        {/* YouTube video box */}
        <div className="nm_media_box nm_youtube_box">
          <h3>Add YouTube Video</h3>
          <div className="nm_media_content">
            {!showYoutubeInput ? (
              <>
                <button 
                  className="nm_media_button" 
                  onClick={() => setShowYoutubeInput(true)}
                >
                  <FaYoutube /> Add Video
                </button>
                <p className="nm_media_description">
                  Embed a YouTube video in your note
                </p>
              </>
            ) : (
              <div className="nm_youtube_input_wrapper">
                <input
                  type="text"
                  className="nm_youtube_input"
                  placeholder="Paste YouTube URL here..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  autoFocus
                />
                <div className="nm_youtube_actions">
                  <button 
                    className="nm_youtube_add_button"
                    onClick={addYoutubeVideo}
                  >
                    Add
                  </button>
                  <button 
                    className="nm_youtube_cancel_button"
                    onClick={() => {
                      setYoutubeUrl('');
                      setShowYoutubeInput(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal />
    </div>
  );
});

// # Set display name for debugging
NoteMedia.displayName = 'NoteMedia';

// # Export component
export default NoteMedia;
